import { autoLoginPartialRoutesGuard } from "angular-auth-oidc-client";

import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";

import { SubscriptionDetailTab } from "../../components/subscription-detail/page-tab/tab-types";
import { AvailabilityExportPage } from "../../pages/availability-export/availability-export";
import { CertPage } from "../../pages/cert/cert";
import { DashboardPage } from "../../pages/dashboard/dashboard";
import { IotNetPage } from "../../pages/iotnet/iotnet";
import { IPPrefixesPage } from "../../pages/ip-prefixes/ip-prefixes";
import { LoginPage } from "../../pages/login/login";
import { MalfunctionPage } from "../../pages/malfunction/malfunction";
import { NotificationSettingsPage } from "../../pages/notification-settings/notification-settings";
import { FirewallSubscriptionComponent } from "../../pages/subscription-detail/components/firewall-subscription/firewall-subscription";
import { IpBgpSubscriptionComponent } from "../../pages/subscription-detail/components/ip-subscription/ip-bgp-subscription";
import { IpStaticSubscriptionComponent } from "../../pages/subscription-detail/components/ip-subscription/ip-static-subscription";
import { IrbSubscriptionComponent } from "../../pages/subscription-detail/components/irb-subscription/irb-subscription";
import { L2vpnSubscription } from "../../pages/subscription-detail/components/l2vpn-subscription/l2vpn-subscription";
import { L3vpnSubscription } from "../../pages/subscription-detail/components/l3vpn-subscription/l3vpn-subscription";
import { LightpathRedundantSubscriptionComponent } from "../../pages/subscription-detail/components/lightpath-subscription/lightpath-redundant-subscription";
import { LightpathSubscriptionComponent } from "../../pages/subscription-detail/components/lightpath-subscription/lightpath-subscription";
import { AggspSubscriptionComponent } from "../../pages/subscription-detail/components/port-subscription/aggsp-subscription";
import { MscSubscriptionComponent } from "../../pages/subscription-detail/components/port-subscription/msc-subscription";
import { PortSubscriptionComponent } from "../../pages/subscription-detail/components/port-subscription/port-subscription";
import { WiredSubscriptionComponent } from "../../pages/subscription-detail/components/wired-subscription/wired-subscription";
import { WirelessSubscriptionComponent } from "../../pages/subscription-detail/components/wireless-subscription/wireless-subscription";
import { SubscriptionDetailRedirectionPage } from "../../pages/subscription-detail/subscription-detail-redirection";
import { SubscriptionsPage } from "../../pages/subscriptions/subscriptions";
import { WirelessSubscriptionsPage } from "../../pages/subscriptions/wireless-subscriptions";
import { SupportPage } from "../../pages/support/support";
import { servicesListPageTitleResolver, translateTitleResolver } from "../../resolvers/translate-title-resolver";
import { ENV } from "../app.runtime";
import { AuthGuard } from "../auth/guard";

const OIDC_GUARD = (ENV.AUTH_ENABLED ?? true) ? autoLoginPartialRoutesGuard : () => true;

export const routes: Routes = [
  { path: "login", component: LoginPage },
  { path: "oauth2/callback", component: LoginPage },

  // --- Service detail pages (canonical URLs under /diensten/) ---
  {
    path: "diensten/FW/FW/:subscriptionId",
    redirectTo: `diensten/FW/:subscriptionId/${SubscriptionDetailTab.CONFIG}`,
    pathMatch: "full",
  },
  {
    path: "diensten/FW/:subscriptionId",
    redirectTo: `diensten/FW/:subscriptionId/${SubscriptionDetailTab.CONFIG}`,
    pathMatch: "full",
  },
  {
    path: "diensten/FW/:subscriptionId/:activeTab",
    component: FirewallSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/Wired/:subscriptionId",
    component: WiredSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/Wireless/:subscriptionId",
    component: WirelessSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/L2VPN/:subscriptionId",
    redirectTo: `diensten/L2VPN/:subscriptionId/${SubscriptionDetailTab.CONFIG}`,
    pathMatch: "full",
  },
  {
    path: "diensten/L2VPN/:subscriptionId/:activeTab",
    component: L2vpnSubscription,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/L3VPN/:subscriptionId",
    redirectTo: `diensten/L3VPN/:subscriptionId/${SubscriptionDetailTab.CONFIG}`,
    pathMatch: "full",
  },
  {
    path: "diensten/L3VPN/:subscriptionId/:activeTab",
    component: L3vpnSubscription,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/IP/IPBGP/:subscriptionId",
    redirectTo: `diensten/IP/IPBGP/:subscriptionId/${SubscriptionDetailTab.CONFIG}`,
  },
  {
    path: "diensten/IP/IPBGP/:subscriptionId/:activeTab",
    component: IpBgpSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/IP/IPS/:subscriptionId",
    redirectTo: `diensten/IP/IPS/:subscriptionId/${SubscriptionDetailTab.CONFIG}`,
  },
  {
    path: "diensten/IP/IPS/:subscriptionId/:activeTab",
    component: IpStaticSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/LightPath/LP/:subscriptionId",
    redirectTo: `diensten/LightPath/LP/:subscriptionId/${SubscriptionDetailTab.CONFIG}`,
  },
  {
    path: "diensten/LightPath/LP/:subscriptionId/:activeTab",
    component: LightpathSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/LightPath/LR/:subscriptionId",
    redirectTo: `diensten/LightPath/LR/:subscriptionId/${SubscriptionDetailTab.CONFIG}`,
  },
  {
    path: "diensten/LightPath/LR/:subscriptionId/:activeTab",
    component: LightpathRedundantSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/Port/SP/:subscriptionId",
    component: PortSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/Port/MSC/:subscriptionId",
    component: MscSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/Port/AGGSP/:subscriptionId",
    component: AggspSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/Port/SPNL/:subscriptionId",
    component: PortSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/Port/MSCNL/:subscriptionId",
    component: MscSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/Port/IRBSP/:subscriptionId",
    component: IrbSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "diensten/Port/AGGSPNL/:subscriptionId",
    component: AggspSubscriptionComponent,
    canActivate: [OIDC_GUARD, AuthGuard],
  },

  // --- Generic service lookup by UUID: /subscription/<uuid> ---
  {
    path: "subscription/:subscriptionId",
    component: SubscriptionDetailRedirectionPage,
    canActivate: [OIDC_GUARD, AuthGuard],
  },

  // --- Service list pages ---
  {
    path: "ip-prefixes",
    component: IPPrefixesPage,
    canActivate: [OIDC_GUARD, AuthGuard],
    title: servicesListPageTitleResolver,
    data: { productType: "IP_PREFIX" },
  },
  {
    path: "diensten/Wireless",
    component: WirelessSubscriptionsPage,
    canActivate: [OIDC_GUARD, AuthGuard],
    title: servicesListPageTitleResolver,
  },
  {
    path: "diensten/:productType",
    component: SubscriptionsPage,
    canActivate: [OIDC_GUARD, AuthGuard],
    title: servicesListPageTitleResolver,
  },

  {
    path: "support",
    component: SupportPage,
    title: () => translateTitleResolver("Navigation.Support"),
  },
  {
    path: "storing-en-onderhoud",
    component: MalfunctionPage,
    canActivate: [OIDC_GUARD, AuthGuard],
    title: () => translateTitleResolver("Navigation.MalfunctionAndMaintenance"),
  },
  {
    path: "storing-en-onderhoud/:productType/:subscriptionId",
    component: MalfunctionPage,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "storing-en-onderhoud/:productType",
    component: MalfunctionPage,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  { path: "inloggen.php", redirectTo: "/" },
  {
    path: "availability-export/:type/:tag/:id",
    component: AvailabilityExportPage,
    canActivate: [OIDC_GUARD],
  },
  {
    path: "notification-settings",
    component: NotificationSettingsPage,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "cert",
    component: CertPage,
    canActivate: [OIDC_GUARD, AuthGuard],
    title: () => translateTitleResolver("Subscription.Cert.Name"),
  },
  {
    path: "cert/:initialTab",
    component: CertPage,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "iotnet",
    component: IotNetPage,
    canActivate: [OIDC_GUARD, AuthGuard],
  },
  {
    path: "",
    component: DashboardPage,
    canActivate: [OIDC_GUARD],
    title: () => translateTitleResolver("Navigation.Subscriptions"),
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class DashboardRoutingModule {}
